<template>
  <div
    class="font-inter h-[1040px] mt-3 rounded-t-[20px] bg-primaryLight pb-10"
  >
    <NavbarComponent />

    <div
      class="h-16 my-2 px-8 bg-primaryLight space-y-6 flex items-center justify-between"
    >
      <div class="space-y-3">
        <h1 v-if="loading">Loading...</h1>
        <h1 v-else class="text-[20px] text-bodyText">
          Welcome back {{ user.first_name }},
        </h1>
        <p class="text-[14px] text-grayDark">
          Get a quick snapshot of platform metrics, user activity, and system insights
        </p>
      </div>

    </div>

    <div class="px-8 space-y-6">
      <!-- Admin Metrics pill -->
      <div
        class="rounded-full px-6 py-3 flex items-center justify-between mx-auto"
        style="
          border: 1px solid #eeeeee;
          border-radius: 100px;
          background: linear-gradient(to bottom, #ffffff, #f3f4f6);
          max-width: 960px;
        "
      >
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingStats ? "--" : totalUsers
          }}</span>
          <span class="text-[12px] text-grayDark">Registered Users</span>
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingStats ? "--" : totalLearningGoals
          }}</span>
          <span class="text-[12px] text-grayDark">Learning Goals</span>
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingStats ? "--" : totalRegularProjects
          }}</span>
          <span class="text-[12px] text-grayDark">Projects</span>
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingStats ? "--" : activeTalents
          }}</span>
          <span class="text-[12px] text-grayDark">Active Talents</span>
        </div>
      </div>

      <!-- Main cards -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- User Statistics Card -->
        <div
          class="bg-primaryLight rounded-[12px] border border-grayLight p-6 space-y-4"
        >
          <div class="flex items-center justify-between">
            <h3 class="text-[16px] font-semibold text-bodyText">
              User Statistics
            </h3>
            <svg
              class="w-6 h-6 text-primaryDark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Admin Users</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalAdminUsers
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Talents</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalTalents
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Investors</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalInvestors
              }}</span>
            </div>
          </div>
        </div>

        <!-- Learning Goals Card -->
        <div
          class="bg-primaryLight rounded-[12px] border border-grayLight p-6 space-y-4"
        >
          <div class="flex items-center justify-between">
            <h3 class="text-[16px] font-semibold text-bodyText">
              Learning Goals
            </h3>
            <svg
              class="w-6 h-6 text-primaryDark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Active</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalActiveLearningGoals
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Completed</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalCompletedLearningGoals
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Failed</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalFailedLearningGoals
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Completion Rate</span>
              <span class="text-[16px] font-semibold text-bodyText"
                >{{ loadingStats ? "--" : completionRate }}%</span
              >
            </div>
          </div>
        </div>

        <!-- Platform Activity Card -->
        <div
          class="bg-primaryLight rounded-[12px] border border-grayLight p-6 space-y-4"
        >
          <div class="flex items-center justify-between">
            <h3 class="text-[16px] font-semibold text-bodyText">
              Platform Activity
            </h3>
            <svg
              class="w-6 h-6 text-primaryDark"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
              />
            </svg>
          </div>
          <div class="space-y-3">
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Total Activities (30d)</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : totalActivities
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Active Talents (30d)</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : uniqueActiveTalents
              }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-[14px] text-grayDark">Avg Progress</span>
              <span class="text-[16px] font-semibold text-bodyText">{{
                loadingStats ? "--" : averageProgress
              }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Users & Projects Section -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Recent Users -->
        <div
          class="bg-primaryLight rounded-[12px] border border-grayLight p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-[16px] font-semibold text-bodyText">
              Recent Users
            </h3>
            <button
              @click="goToUsers"
              class="text-[14px] text-primaryDark hover:underline"
            >
              View All
            </button>
          </div>
          <div v-if="loadingUsers" class="text-center py-4">
            <p class="text-grayDark">Loading users...</p>
          </div>
          <div v-else-if="recentUsers.length === 0" class="text-center py-4">
            <p class="text-grayDark">No users found</p>
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="user in recentUsers"
              :key="user.user_id"
              class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              @click="goToUserProfile(user.user_id)"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-10 h-10 rounded-full bg-primaryDark text-white flex items-center justify-center font-semibold"
                >
                  {{ getInitials(user.first_name, user.last_name) }}
                </div>
                <div>
                  <p class="text-[14px] font-medium text-bodyText">
                    {{ user.first_name }} {{ user.last_name }}
                  </p>
                  <p class="text-[12px] text-grayDark">{{ user.email }}</p>
                </div>
              </div>
              <span
                class="text-[12px] px-2 py-1 rounded-full"
                :class="getRoleBadgeClass(user.role)"
              >
                {{ getRoleLabel(user.role) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Recent Projects -->
        <div
          class="bg-primaryLight rounded-[12px] border border-grayLight p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-[16px] font-semibold text-bodyText">
              Recent Projects
            </h3>
            <button
              @click="goToProjects"
              class="text-[14px] text-primaryDark hover:underline"
            >
              View All
            </button>
          </div>
          <div v-if="loadingProjects" class="text-center py-4">
            <p class="text-grayDark">Loading projects...</p>
          </div>
          <div
            v-else-if="recentProjects.length === 0"
            class="text-center py-4"
          >
            <p class="text-grayDark">No projects found</p>
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="project in recentProjects"
              :key="project.project_id"
              class="p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              @click="goToProject(project.project_id)"
            >
              <div class="flex items-center justify-between mb-2">
                <p class="text-[14px] font-medium text-bodyText">
                  {{ project.project_name }}
                </p>
                <span
                  class="text-[12px] px-2 py-1 rounded-full"
                  :class="getStatusBadgeClass(project.status)"
                >
                  {{ project.status }}
                </span>
              </div>
              <p class="text-[12px] text-grayDark line-clamp-2">
                {{ project.description || "No description available" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import NavbarComponent from "@/components/NavbarComponent.vue";
import eventBus from "@/eventBus";

export default {
  name: "AdminDashboardComponent",
  components: {
    NavbarComponent,
  },
  data() {
    return {
      user: {},
      loading: true,
      loadingStats: true,
      loadingUsers: true,
      loadingProjects: true,
      // Statistics
      totalUsers: 0,
      totalLearningGoals: 0,
      totalRegularProjects: 0,
      activeTalents: 0,
      // User Statistics
      totalAdminUsers: 0,
      totalTalents: 0,
      totalInvestors: 0,
      // Learning Goal Statistics
      totalCompletedLearningGoals: 0,
      totalActiveLearningGoals: 0,
      totalFailedLearningGoals: 0,
      completionRate: 0,
      totalActivities: 0,
      uniqueActiveTalents: 0,
      averageProgress: 0,
      // Recent data
      recentUsers: [],
      recentProjects: [],
    };
  },
  methods: {
    emitOpenCreateProjectModal() {
      eventBus.emit("openCreateProjectModal");
    },

    async fetchCurrentUser() {
      try {
        const response = await apiClient.post("/current_user");
        this.user = response.data.user || {};
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminStats() {
      try {
        this.loadingStats = true;
        
        const overviewResponse = await apiClient.get("/web/dashboard/admin/overview");
        const overview = overviewResponse.data || {};
        this.totalUsers = overview.total_users || 0;
        this.totalLearningGoals = overview.total_learning_goals || 0;
        this.totalRegularProjects = overview.total_regular_projects || 0;
        this.activeTalents = overview.total_active_talents || 0;

        const userStatsResponse = await apiClient.get("/web/dashboard/admin/user-statistics");
        const userStats = userStatsResponse.data || {};
        this.totalAdminUsers = userStats.total_admin_users || 0;
        this.totalTalents = userStats.total_talents || 0;
        this.totalInvestors = userStats.total_investors || 0;

        const goalStatsResponse = await apiClient.get("/web/dashboard/admin/learning-goal-statistics");
        const goalStats = goalStatsResponse.data || {};
        this.totalCompletedLearningGoals = goalStats.total_completed_learning_goals || 0;
        this.totalActiveLearningGoals = goalStats.total_active_learning_goals || 0;
        this.totalFailedLearningGoals = goalStats.total_failed_learning_goals || 0;
        this.completionRate = goalStats.completion_rate_percentage || 0;

        const activityStatsResponse = await apiClient.get("/web/dashboard/admin/activity-statistics", {
          params: { days: 30 }
        });
        const activityStats = activityStatsResponse.data || {};
        this.totalActivities = activityStats.total_activities || 0;
        this.uniqueActiveTalents = activityStats.unique_active_talents || 0;
        this.averageProgress = activityStats.average_progress_percentage || 0;
        
      } catch (error) {
        console.error("Error fetching admin statistics:", error);
      } finally {
        this.loadingStats = false;
      }
    },

    async fetchRecentUsers() {
      try {
        this.loadingUsers = true;
        const response = await apiClient.get("/web/users");
        const users = response.data.data || [];
        // Get the 5 most recent users
        this.recentUsers = users
          .sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          })
          .slice(0, 5);
      } catch (error) {
        console.error("Error fetching recent users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    async fetchRecentProjects() {
      try {
        this.loadingProjects = true;
        const response = await apiClient.get("/web/projects_all");
        const projects = response.data.data || [];
        // Get the 5 most recent projects
        this.recentProjects = projects
          .sort((a, b) => {
            const dateA = new Date(a.created_at || 0);
            const dateB = new Date(b.created_at || 0);
            return dateB - dateA;
          })
          .slice(0, 5);
      } catch (error) {
        console.error("Error fetching recent projects:", error);
      } finally {
        this.loadingProjects = false;
      }
    },

    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : "";
      const last = lastName ? lastName.charAt(0).toUpperCase() : "";
      return first + last || "U";
    },

    getRoleLabel(roles) {
      if (!roles || !Array.isArray(roles) || roles.length === 0) return "User";
      // Return the first role
      return roles[0].charAt(0).toUpperCase() + roles[0].slice(1);
    },

    getRoleBadgeClass(roles) {
      if (!roles || !Array.isArray(roles) || roles.length === 0)
        return "bg-gray-100 text-gray-700";
      const role = roles[0];
      switch (role) {
        case "admin":
          return "bg-red-100 text-red-700";
        case "manager":
          return "bg-blue-100 text-blue-700";
        case "talent":
          return "bg-green-100 text-green-700";
        case "investor":
          return "bg-purple-100 text-purple-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    },

    getStatusBadgeClass(status) {
      const statusLower = status?.toLowerCase() || "";
      switch (statusLower) {
        case "completed":
          return "bg-green-100 text-green-700";
        case "in progress":
          return "bg-blue-100 text-blue-700";
        case "on hold":
          return "bg-yellow-100 text-yellow-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    },

    goToUsers() {
      this.$router.push({ name: "UserList" });
    },

    goToProjects() {
      this.$router.push({ name: "Projects" });
    },

    goToUserProfile(userId) {
      this.$router.push({ name: "Profile", params: { userId } });
    },

    goToProject(projectId) {
      this.$router.push({ name: "ProjectDetails", params: { projectId } });
    },
  },

  mounted() {
    this.fetchCurrentUser();
    this.fetchAdminStats();
    this.fetchRecentUsers();
    this.fetchRecentProjects();
  },
};
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
